import { apiKey, apiProjectKey } from "@/config";
import { isElement } from "hast-util-is-element";
import { useEffect, useState } from "react";
import rehypeParse from "rehype-parse";
import { unified } from "unified";
import { debug } from "./debug";
import {
  hasProperty,
  inlineScript,
  isText,
  loadScript,
  loadStyle,
} from "./util";

export type MapApiInstance = {
  map: (
    mapDiv: HTMLDivElement,
    mapConfig: Record<string, unknown>
  ) => MapInstance;
  geomLayer: (
    content: Travelguide.ID | Array<Travelguide.ID>,
    layerConfig: MapLayerConfig
  ) => MapLayerInstance;
};

export type MapLibraryInstance = (
  callback: (oamaps: MapApiInstance) => void
) => undefined;

export type MapInstance = unknown;
export interface MapLayerInstance {
  whenLoaded: (callback: () => void) => void;
  getBounds: () => unknown;
  setMap: (map: MapInstance) => void;
}

export interface MapLayerConfig {
  fitPoiBounds?: boolean;
  fitTourBounds?: boolean;
  markersActive: boolean;
  defaultIW?: boolean;
}

function useLoadMapLibraries({
  projectKey,
  apiKey,
  type,
}: {
  projectKey: string;
  apiKey: string;
  type: "oa" | "oax";
}) {
  const [loaded, setLoaded] = useState<Array<string>>([]);
  const resolveLoaded = (resolve: string) => setLoaded(loaded.concat(resolve));

  useEffect(() => {
    const src = `https://api-oa.com/jscr/${type}_head.js?proj=${projectKey}&key=${apiKey}&lang=de`;

    if (src in loaded) {
      return;
    }

    loadScript(src, true).then(resolveLoaded);

    const documentWriteAttempts: Array<string> = [];

    document.write = (arg) => {
      debug("warn")("document.write detected");

      if (documentWriteAttempts.includes(arg)) {
        debug("log")("document.write already attempted, returning");
        return;
      }
      documentWriteAttempts.push(arg);

      debug("log")("analyzing d.write:", arg);

      const tree = unified().use(rehypeParse, { fragment: true }).parse(arg);

      const script = tree.children.find(
        (el) => isElement(el) && el.tagName === "script"
      );

      if (isElement(script)) {
        if (hasProperty(script, "src")) {
          debug("log")("loading script:", script.properties.src);
          loadScript(script.properties.src).then(resolveLoaded);
        } else {
          const text = script.children.filter(isText).map((el) => el.value);
          debug("log")("analyzing script:");
          debug("dir")(text);
          inlineScript(text.join("\n")).then(resolveLoaded);
        }
      }

      const link = tree.children.find(
        (el) => isElement(el) && el.tagName === "link"
      );
      if (isElement(link)) {
        if (hasProperty(link, "href")) {
          debug("log")("loading stylesheet:", link.properties.href);
          loadStyle(link.properties.href).then(resolveLoaded);
        } else {
          const text = link.children.filter(isText).map((el) => el.value);
          debug("log")("analyzing stylesheet:");
          debug("dir")(text);
        }
      }
    };
  }, []);

  const [oa, oax] = (["oa", "oax"] as Array<"oa" | "oax">).map((key) =>
    key in globalThis && globalThis[key].api && globalThis[key].api.maps
      ? (globalThis[key].api.maps as MapLibraryInstance)
      : undefined
  );

  return oa && oax ? { oa, oax } : undefined;
}

export function useMapApi(type: "oa" | "oax" = "oa") {
  const libraries = useLoadMapLibraries({
    projectKey: apiProjectKey,
    apiKey,
    type,
  });

  const [api, setApi] = useState<MapApiInstance | undefined>(undefined);

  useEffect(() => {
    if (libraries) {
      libraries[type](setApi);
    }
  }, [libraries]);

  return api;
}
